/**
 * The whole board as a paper somebody can put on a table.
 *
 * This document was produced once by hand, outside the tool, and the point of
 * the tool is that nobody should have to do that. A representative presses one
 * button, the browser's own print dialogue makes the PDF, and the paper is
 * dated the moment it was asked for.
 *
 * No PDF is generated on the server. Doing that needs a browser engine in the
 * API container to do a job every reader's browser already does well, and it
 * would put a headless Chromium into the deployment for one button.
 *
 * Three things about the shape, all of them from watching the first version
 * fail on paper:
 *
 * **Each claim is five things in a fixed order** — what the agreement says now,
 * the demand, and the reasoning, under a heading that names the clauses. That
 * order is the argument: a reader who has just read the clause is ready for the
 * ask, and a reader who has just read the ask wants the evidence. Reversing any
 * pair makes the page read as an assertion.
 *
 * **A claim quoting nothing says so in words.** Fourteen claims have no clause
 * to quote because the agreement is silent, and an empty box under "what the
 * agreement says now" reads as a missing field rather than as the finding.
 *
 * **Nothing breaks across a page if it can be helped.** `break-inside: avoid`
 * on each item, because a demand on one page and its reasoning on the next is
 * how a claim gets quoted without its evidence.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

const TYPE: Record<string, string> = {
  new: 'New term',
  improve: 'Improve an existing term',
  protect: 'Protect an existing term',
  correct: 'Correct a drafting error',
};

function Paragraphs({ text }: { text: string | null }) {
  const parts = (text ?? '').split('\n').filter((p) => p.trim() !== '');
  return (
    <>
      {parts.map((p, i) => (
        <p key={i} className="paper-p">
          {p}
        </p>
      ))}
    </>
  );
}

function Item({
  ref_,
  title,
  meta,
  verbatim,
  silence,
  demand,
  why,
}: {
  ref_: string;
  title: string;
  meta: string;
  verbatim: string | null;
  silence: string;
  demand: string;
  why: React.ReactNode;
}) {
  return (
    <section className="paper-item">
      <h2 className="paper-h2">
        <span className="paper-ref">{ref_}</span> {title}
      </h2>
      <p className="paper-meta">{meta}</p>

      <h3 className="paper-h3">What the agreement says now</h3>
      {verbatim && verbatim.trim() !== '' ? (
        <blockquote className="paper-quote">{verbatim}</blockquote>
      ) : (
        <p className="paper-silence">{silence}</p>
      )}

      <h3 className="paper-h3">The claim</h3>
      <p className="paper-demand">{demand}</p>

      <h3 className="paper-h3">Why</h3>
      <div>{why}</div>
    </section>
  );
}

export function ClaimsPaper({ onClose }: { onClose: () => void }) {
  const paper = useQuery({ queryKey: ['claims-paper'], queryFn: api.claimsPaper });

  if (paper.isPending) return <p className="label p-6">Building the paper…</p>;
  if (!paper.data) return <p className="label p-6">The paper could not be built.</p>;

  const { claims, protect, watermark } = paper.data;
  const noFloor = protect.filter((p) => p.exposure === 'full').length;
  const today = new Date().toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="paper-root">
      {/* Hidden when printing: the controls are not part of the document. */}
      <div className="paper-controls">
        <button type="button" onClick={() => window.print()} className="hit paper-print">
          Print or save as PDF
        </button>
        <button type="button" onClick={onClose} className="hit label underline">
          Back to the claims
        </button>
        <span className="label">
          {claims.length} claims · {protect.length} terms to protect
        </span>
      </div>

      <div className="paper-cover">
        <h1 className="paper-title">Our claims</h1>
        <p className="paper-sub">
          Tyndale Christian School · Teachers Multi-Enterprise Agreement 2023
        </p>
        <dl className="paper-dl">
          <dt>Prepared</dt>
          <dd>{today}</dd>
          <dt>Contents</dt>
          <dd>
            {claims.length} claims, and {protect.length} existing terms to protect
          </dd>
        </dl>
        <div className="paper-notice">
          <b>A working paper, not a proposal to the school.</b>
          <p className="paper-p">
            Each claim is a plain statement of what the teachers on the team want, the clause it
            bears on, and the reason. It is not drafted wording — the wording is a lawyer&rsquo;s
            job, and every claim here is expected to be rewritten before it goes anywhere. What must
            survive that rewrite is the demand and the reason for it.
          </p>
          <p className="paper-p">
            The clause text quoted under each claim is the agreement as it stands today. Where a
            claim quotes nothing, the agreement is silent on the subject, and the silence is the
            claim.
          </p>
        </div>
      </div>

      <h1 className="paper-part">Part one — claims</h1>
      <p className="paper-lede">
        What we are asking the school to change, in the order the claims were raised.
      </p>
      {claims.map((c) => (
        <Item
          key={c.ref}
          ref_={c.ref}
          title={c.title}
          meta={[
            TYPE[c.type] ?? c.type,
            c.cites ? `citing ${c.cites}` : 'no clause cited',
            c.category,
            c.owner,
            c.status.replace(/_/g, ' '),
            c.wordingChecked ? null : 'wording not yet checked',
          ]
            .filter(Boolean)
            .join(' · ')}
          verbatim={c.verbatim}
          silence="The agreement contains no provision on this. That silence is the claim."
          demand={c.demand ?? 'No wording yet.'}
          why={<Paragraphs text={c.rationale} />}
        />
      ))}

      <h1 className="paper-part">Part two — existing terms to protect</h1>
      <p className="paper-lede">
        These are terms the agreement already gives and the group would lose if they were traded
        away. {noFloor} of the {protect.length} have no floor beneath them at all: if they come out
        of the agreement, nothing in the Award replaces them.
      </p>
      {protect.map((p) => (
        <Item
          key={p.number}
          ref_={p.number}
          title={p.heading ?? 'Untitled'}
          meta={[
            p.exposure === 'full' ? 'No Award floor' : 'Award floor is lower',
            p.awardNumber
              ? `compared against Award ${p.awardNumber}${p.awardHeading ? ` — ${p.awardHeading}` : ''}`
              : 'no matching Award provision was found',
            p.verified ? 'a person has checked this comparison' : 'nobody has checked this yet',
          ].join(' · ')}
          verbatim={p.verbatim}
          silence="The clause text could not be read from the corpus."
          demand="Keep this term as it stands."
          why={
            <>
              <p className="paper-p">
                {p.exposure === 'full'
                  ? 'The Award floor has nothing on this — if it goes from the agreement, nothing underneath replaces it.'
                  : 'The Award floor is lower than what the agreement gives — if it goes, a worse term replaces it.'}
              </p>
              <Paragraphs text={p.note} />
            </>
          }
        />
      ))}

      {/* Last, and on the paper rather than only in the log: a document that
          leaves the building should say who printed it and when. */}
      <p className="paper-mark">{watermark}</p>
    </div>
  );
}
